import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Clock } from "lucide-react";
import { Results } from "../components/Results";
import { ExpiredReading } from "../components/ExpiredReading";
import { BrandMark } from "../components/BrandMark";
import { Footer } from "../components/Footer";
import { fetchShare } from "../lib/api";

export const SharedReadingPage = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    fetchShare(token)
      .then((data) => {
        if (cancelled) return;
        const augmented = {
          ...data,
          _meta: {
            date_of_birth: data.date_of_birth,
            time_of_birth: data.time_of_birth,
            place_of_birth: data.place_of_birth,
          },
        };
        document.title = `${data.name}'s Astrarium reading`;
        setState({ loading: false, data: augmented, error: null });
      })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 410 || status === 404) {
          setState({ loading: false, data: null, error: "expired" });
        } else {
          setState({ loading: false, data: null, error: "fetch" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const daysLeft = state.data?.expires_at
    ? Math.max(0, Math.ceil((new Date(state.data.expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-30 px-6 py-5 flex items-center justify-center pointer-events-none">
        <div className="pointer-events-auto">
          <Link to="/"><BrandMark /></Link>
        </div>
      </header>

      <main className="relative z-10" data-testid="shared-reading-page">
        {state.loading && (
          <section className="min-h-[80vh] flex items-center justify-center px-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center text-center"
              data-testid="shared-loading"
            >
              <Loader2 className="h-6 w-6 text-stardust animate-spin mb-4" />
              <p className="font-serif-italic text-slate-300 text-lg">Drawing the shared almanac…</p>
            </motion.div>
          </section>
        )}

        {!state.loading && state.error === "expired" && <ExpiredReading />}

        {!state.loading && state.error === "fetch" && (
          <section className="min-h-[80vh] flex flex-col items-center justify-center px-6 text-center" data-testid="shared-error">
            <p className="text-stardust/70 uppercase tracking-[0.5em] text-[10px] mb-5">A passing cloud</p>
            <h1 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-4">
              We couldn&apos;t reach the stars<span className="text-stardust">.</span>
            </h1>
            <p className="text-slate-400 max-w-md mb-8 leading-relaxed">
              Something went wrong loading this shared reading. Please check the link or try again.
            </p>
            <Link
              to="/"
              className="bg-stardust hover:bg-stardust-light text-cosmic-900 px-8 py-4 rounded-md uppercase tracking-[0.3em] text-xs font-medium"
            >
              Get my own reading
            </Link>
          </section>
        )}

        {!state.loading && state.data && (
          <>
            <div className="pt-24 pb-2 flex justify-center" data-testid="shared-expiry-banner">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-stardust/[0.06] border border-stardust/20 text-stardust/90 text-[11px] uppercase tracking-[0.25em]">
                <Clock className="h-3 w-3" />
                Shared reading · {daysLeft === 0 ? "expires today" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
              </div>
            </div>
            <Results data={state.data} readOnly />
          </>
        )}
      </main>

      <Footer />
    </>
  );
};
